import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import Button from './Button';

const Hero = ({ 
  title, 
  subtitle, 
  description, 
  primaryCta, 
  secondaryCta,
  backgroundImage,
  className = '' 
}) => {
  return (
    <section className={`relative overflow-hidden ${className}`}>
      {/* Background with gradient overlay */}
      <div className="absolute inset-0">
        {backgroundImage && (
          <div 
            className="absolute inset-0 bg-cover bg-center bg-no-repeat"
            style={{ backgroundImage: `url(${backgroundImage})` }}
          />
        )}
        <div className="absolute inset-0 bg-gradient-to-br from-primary-600/95 via-primary-700/90 to-primary-900/95" />
        
        {/* Animated gradient orbs */}
        <div className="absolute top-0 left-0 w-96 h-96 bg-primary-400/20 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-primary-500/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
      </div>
      
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
        <div className="max-w-4xl mx-auto text-center sm:text-left">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
          >
            {subtitle && (
              <motion.p 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.2 }}
                className="text-primary-100 text-lg mb-4 font-semibold tracking-wide uppercase"
              >
                {subtitle}
              </motion.p>
            )}
            
            <motion.h1 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="text-4xl md:text-6xl lg:text-7xl font-bold leading-tight mb-6 text-white"
            >
              {title}
            </motion.h1>
            
            {description && (
              <motion.p 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="text-xl text-primary-50 mb-8 max-w-2xl mx-auto sm:mx-0 leading-relaxed"
              >
                {description}
              </motion.p>
            )}
            
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="flex flex-col sm:flex-row gap-4 justify-center sm:justify-start"
            >
              {primaryCta && (
                primaryCta.href ? (
                  <Link to={primaryCta.href} className="w-full sm:w-auto">
                    <Button 
                      variant="secondary" 
                      size="lg"
                      onClick={primaryCta.onClick}
                      className="shadow-xl hover:shadow-2xl w-full sm:w-auto"
                    >
                      {primaryCta.text}
                    </Button>
                  </Link>
                ) : (
                  <Button 
                    variant="secondary" 
                    size="lg"
                    onClick={primaryCta.onClick}
                    className="shadow-xl hover:shadow-2xl w-full sm:w-auto"
                  >
                    {primaryCta.text}
                  </Button>
                )
              )}
              
              {secondaryCta && (
                secondaryCta.href ? (
                  <Link to={secondaryCta.href} className="w-full sm:w-auto">
                    <Button 
                      variant="ghost" 
                      size="lg"
                      className="text-white border-2 border-white/80 hover:bg-white hover:text-primary-600 backdrop-blur-sm w-full sm:w-auto"
                      onClick={secondaryCta.onClick}
                    >
                      {secondaryCta.text}
                    </Button>
                  </Link>
                ) : (
                  <Button 
                    variant="ghost" 
                    size="lg"
                    className="text-white border-2 border-white/80 hover:bg-white hover:text-primary-600 backdrop-blur-sm w-full sm:w-auto"
                    onClick={secondaryCta.onClick}
                  >
                    {secondaryCta.text}
                  </Button>
                )
              )}
            </motion.div>
          </motion.div>
        </div>
      </div>
      
      {/* Bottom wave decoration */}
      <div className="absolute bottom-0 left-0 right-0">
        <svg className="w-full h-12 md:h-16 fill-white" viewBox="0 0 1200 120" preserveAspectRatio="none">
          <path d="M321.39,56.44c58-10.79,114.16-30.13,172-41.86,82.39-16.72,168.19-17.73,250.45-.39C823.78,31,906.67,72,985.66,92.83c70.05,18.48,146.53,26.09,214.34,3V0H0V27.35A600.21,600.21,0,0,0,321.39,56.44Z"></path>
        </svg>
      </div>
    </section>
  );
};

export default Hero;